import { ApiOrganizationOrganizationTypeChoices, EhrConnectionStatus } from "@api/graphql/types-and-hooks";

// Org types that integrate via an org-level API rather than per-user
// credentials (no per-clinician login) — the backend always reports these as
// already connected (resolve_ehr_connection → DIRECT_EHR_INTEGRATIONS), so
// onboarding treats them as connected without a credential login step. This
// is independent of whether their client import reports real status — see
// isStatusMaskedOrgType for that.
const DIRECT_INTEGRATION_ORG_TYPES = new Set<ApiOrganizationOrganizationTypeChoices>([
  ApiOrganizationOrganizationTypeChoices.PassageHealth,
  ApiOrganizationOrganizationTypeChoices.Therapz,
  ApiOrganizationOrganizationTypeChoices.HippHealth,
]);

export const isDirectIntegrationOrgType = (
  orgType: ApiOrganizationOrganizationTypeChoices | null | undefined,
): boolean => !!orgType && DIRECT_INTEGRATION_ORG_TYPES.has(orgType);

// Direct-integration org types whose backend EhrConnectionStatus is still
// hardcoded to CONNECTED instead of reflecting real import progress. Passage
// Health and Therapz moved off this list once the backend started reporting
// live status (PROA-2515, see docs/adr/0001-passage-therapz-status-polling.md);
// Hipp Health stays until its status-masking issue is fixed separately.
const STATUS_MASKED_ORG_TYPES = new Set<ApiOrganizationOrganizationTypeChoices>([
  ApiOrganizationOrganizationTypeChoices.HippHealth,
]);

export const isStatusMaskedOrgType = (orgType: ApiOrganizationOrganizationTypeChoices | null | undefined): boolean =>
  !!orgType && STATUS_MASKED_ORG_TYPES.has(orgType);

// Statuses that mean the EHR is connected (or actively working off a live
// connection) for onboarding purposes — anything mid-sync still counts as
// connected. Shared by the Connect-EHR and Import-clients steps.
export const CONNECTED_STATUSES: EhrConnectionStatus[] = [
  EhrConnectionStatus.Connected,
  EhrConnectionStatus.ImportingClientList,
  EhrConnectionStatus.ImportingTreatmentPlan,
  EhrConnectionStatus.ExportingTreatmentPlan,
];

export const isEhrConnected = (status: EhrConnectionStatus | null | undefined): boolean =>
  !!status && CONNECTED_STATUSES.includes(status);
